import { APIFunctions } from '../../lib/apiFunctions'
import { ILLPeriod } from '../../lib/interfaces'
import { getCommonHeaders } from '../../services/http'
import { Session } from '../../types/session'
import { createExcelFile } from './dataCompiler'
import { IReportDataInput, ExcelPeriod } from './types'

async function runUntilLockedReportIsAvailable(
    apiFunctions: APIFunctions,
    callback,
    taskId: string
) {
    while (true) {
        await new Promise((r) => setTimeout(r, 1500))
        const taskStatus = await apiFunctions.getTaskStatus(taskId)
        callback({
            type: 'server_side',
            status: taskStatus.completion_percentage === 100 ? 'success' : 'active',
            meta: { percent: taskStatus.completion_percentage },
        })
        if (taskStatus.completion_percentage >= 100) {
            break
        }
    }
}

async function buildLockedReport(
    apiFunctions: APIFunctions,
    callback,
    llPeriodId: string,
    reportId: string,
    lockedReportId: string
) {
    callback({
        type: 'initializing',
        status: 'success',
        meta: { percent: 100 },
    })
    callback({
        type: 'fetchingData',
        status: 'active',
        meta: { percent: 0 },
    })
    const reportDataRes = await apiFunctions.getReportData(reportId)
    const reportData = reportDataRes.data
    callback({
        type: 'fetchingData',
        status: 'success',
        meta: { percent: 100 },
    })
    function zipCallback(meta) {
        callback({
            type: 'compiling',
            status: 'active',
            meta: meta,
        })
    }
    const llPeriod: ILLPeriod = (await apiFunctions.getLLPeriodSmall(llPeriodId)).period
    const excelPeriod: ExcelPeriod = {
        start: llPeriod.date_start,
        end: llPeriod.date_end,
        dates: reportData.dates,
        earliest_end: llPeriod?.earliest_end,
        has_roster: reportData.has_roster,
        roster_periods: reportData.roster_periods,
    }
    const reportDataForExcel: IReportDataInput = {
        userName: reportData.name,
        data: excelPeriod,
        includeDoc: reportData.include_documentation,
        includeNotes: reportData.include_notes,
        includeLocations: reportData.include_locations,
        disableRosterOverride: !reportData.has_roster,
        reportId,
        full_name: reportData.full_name,
        person_id: reportData.person_id,
    }
    const excelData: Blob = await createExcelFile(reportDataForExcel, zipCallback, true)
    callback({
        type: 'compiling',
        status: 'success',
        meta: { percent: 100 },
    })
    callback({
        type: 'submitData',
        status: 'active',
        meta: { percent: 0 },
    })
    const reportDataForm = new FormData()
    reportDataForm.append('file', excelData)
    const fileArgs: RequestInit = {
        method: 'POST',
        body: reportDataForm,
        headers: {
            ...getCommonHeaders(),
            ...apiFunctions.getAuthHeader(),
        },
    }
    const fileUploadURL: string = apiFunctions.getLockedReportUploadURL(reportId, lockedReportId)
    const reqParams = new Request(fileUploadURL, fileArgs)
    await fetch(reqParams)
    callback({
        type: 'submitData',
        status: 'success',
        meta: { percent: 100 },
    })
}

export async function LockReport(
    session: Session,
    reportId,
    llPeriodId,
    callback,
    apiFunctions: APIFunctions
) {
    callback({
        type: 'initializing',
        status: 'active',
        meta: { percent: 0 },
    })
    const lockResData = await apiFunctions.lockReport(reportId)
    if (lockResData?.task_id != null) {
        await runUntilLockedReportIsAvailable(apiFunctions, callback, lockResData.task_id)
    } else {
        await buildLockedReport(
            apiFunctions,
            callback,
            llPeriodId,
            reportId,
            lockResData.locked_report_id
        )
    }
    return lockResData.locked_report_id
}
